// src/components/PursuitAccess/ExportUserAssessmentsButton.jsx
import React, { useState } from "react";
import { Box, Button, Layer, Text, Heading, RadioButtonGroup, Notification } from "grommet";
import axios from "../../config/adminAxios";
import { getProductSkills } from "../../services/getProductSkills";

const parseCoursesResponse = (data) => {
    const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.courses)
            ? data.courses
            : Array.isArray(data?.data?.courses)
                ? data.data.courses
                : Array.isArray(data?.data)
                    ? data.data
                    : [];
    return list.map((c) => ({
        id: c.id || c.course_id || "",
        title: c.title || c.course_title || c.name || "",
        provider: c.provider || c.platform || c.source || "",
        url: c.url || c.link || c.course_url || "",
    }));
};

const getCoursesForUser = async (userId, assessmentId) => {
    if (!userId) return [];
    try {
        const { data } = await axios.get(
            `/api/dashboard/getAllCoursesByUserAssessment/${userId}/${assessmentId}`
        );
        return parseCoursesResponse(data);
    } catch {
        return [];
    }
};

const RANGES = [
    { label: "1 day", value: "1d", days: 1 },
    { label: "1 week", value: "1w", days: 7 },
    { label: "1 month", value: "1m", days: 30 },
    { label: "3 months", value: "3m", days: 90 },
    { label: "6 months", value: "6m", days: 180 },
];

const fmtDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }) : "";

const withinRange = (endISO, value) => {
    const spec = RANGES.find((r) => r.value === value);
    if (!spec || !endISO) return true;
    const end = new Date(endISO);
    const since = new Date(Date.now() - spec.days * 24 * 3600 * 1000);
    return end >= since;
};

const rowsToCSV = (rows) => {
    const headers = Object.keys(rows[0] || {});
    const esc = (v) => {
        const s = (v ?? "").toString();
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [headers.map(esc).join(","), ...rows.map((r) => headers.map((h) => esc(r[h])).join(","))];
    return lines.join("\n");
};

const downloadBlob = (filename, mime, content) => {
    const blob = content instanceof Blob ? content : new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};

async function writeXlsx(sheets) {
    const { default: ExcelJS } = await import("exceljs");
    const wb = new ExcelJS.Workbook();
    for (const { name, rows } of sheets) {
        const ws = wb.addWorksheet(name);
        if (rows.length) {
            ws.columns = Object.keys(rows[0]).map((key) => ({ header: key, key }));
            rows.forEach((r) => ws.addRow(r));
        }
    }
    const buffer = await wb.xlsx.writeBuffer();
    return new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
}

const ExportUserAssessmentsButton = ({ apiBase, assessmentIds = [], userId, defaultRange = "6m" }) => {
    const [open, setOpen] = useState(false);
    const [range, setRange] = useState(defaultRange);
    const [toast, setToast] = useState(null);

    const buildRow = (summary, products, courses) => {
        const productList = (products || []).map((p) => p.product_name).filter(Boolean);
        const skillList = (products || []).map((p) => p.skill_name).filter(Boolean);
        const courseTitles = (courses || []).map((c) => c.title).filter(Boolean).join("; ");

        return {
            User: summary?.name || "",
            Email: summary?.email || summary?.user_email || "",
            "Start Date": fmtDate(summary?.start_date),
            "Start Time": summary?.start_time || "",
            "End Date": fmtDate(summary?.end_date),
            "End Time": summary?.end_time || "",
            Questions: summary?.query_count ?? "",
            Correct: summary?.correct_count ?? "",
            Incorrect: summary?.incorrect_count ?? "",
            Products: productList.join("; "),
            Skills: skillList.join("; "),
            "Recommended courses": courseTitles,
        };
    };

    const handleConfirm = async () => {
        setOpen(false);

        const fetchOne = async (id) => {
            const [sumRes, prodRes, coursesRes] = await Promise.allSettled([
                axios.get(`${apiBase}/manager/assessmentSummary/${id}`),
                userId
                    ? getProductSkills({ user_id: userId, assessment_id: id })
                    : Promise.resolve({ data: { subProducts: [] } }),
                getCoursesForUser(userId, id),
            ]);
            const summary =
                sumRes.status === "fulfilled" ? sumRes.value?.data?.assessmentSummary?.[0] ?? null : null;
            const products =
                prodRes.status === "fulfilled" ? prodRes.value?.data?.subProducts ?? [] : [];
            const courses = coursesRes.status === "fulfilled" ? coursesRes.value : [];
            return { id, summary, products, courses };
        };

        const settled = await Promise.allSettled(assessmentIds.map(fetchOne));
        const bundles = settled
            .map((s) => (s.status === "fulfilled" ? s.value : null))
            .filter(Boolean);

        const rows = bundles
            .filter((b) => withinRange(b.summary?.end_date, range))
            .map((b) => buildRow(b.summary, b.products, b.courses));

        if (!rows.length) {
            setToast({ status: "warning", title: "Export Warning", message: "No data for selected date range." });
            return;
        }

        const stamp = new Date().toISOString().slice(0, 10);
        const firstSummary = bundles[0]?.summary || {};
        const emailForName = firstSummary.email || firstSummary.user_email || "user";
        const baseName = `HPE Digital Skill Advisor - User assessments - ${emailForName} - ${range} - ${stamp}`;

        try {
            const blob = await writeXlsx([{ name: "Assessments", rows }]);
            downloadBlob(`${baseName}.xlsx`, null, blob);
            setToast({
                status: "normal",
                title: "Export Complete",
                message: `Downloaded ${rows.length} assessment${rows.length > 1 ? "s" : ""}.`,
            });
        } catch {
            const csv = rowsToCSV(rows);
            downloadBlob(`${baseName}.csv`, "text/csv;charset=utf-8", csv);
            setToast({
                status: "normal",
                title: "Export Complete",
                message: `Downloaded CSV for ${rows.length} assessment${rows.length > 1 ? "s" : ""}.`,
            });
        }
    };

    return (
        <>
            <Button label="Export" onClick={() => setOpen(true)} secondary disabled={!assessmentIds.length} />

            {open && (
                <Layer onEsc={() => setOpen(false)} onClickOutside={() => setOpen(false)} modal responsive={false} position="center">
                    <Box pad="medium" width="medium" gap="small">
                        <Heading level={3} margin="none">Confirm Export</Heading>
                        <Text>All the data for the past period will be downloaded.</Text>

                        <Box gap="xsmall" margin={{ top: "xsmall" }}>
                            <Text weight="bold" size="small">Select date range</Text>
                            <RadioButtonGroup
                                name="exportRange"
                                options={RANGES.map((r) => ({ label: r.label, value: r.value }))}
                                value={range}
                                onChange={(e) => setRange(e.target.value)}
                            />
                        </Box>

                        <Box direction="row" gap="small" justify="end" margin={{ top: "small" }}>
                            <Button label="No" onClick={() => setOpen(false)} />
                            <Button label="Yes" primary onClick={handleConfirm} />
                        </Box>
                    </Box>
                </Layer>
            )}

            {toast && (
                <Notification
                    toast
                    status={toast.status}
                    title={toast.title}
                    message={toast.message}
                    onClose={() => setToast(null)}
                />
            )}
        </>
    );
};

export default ExportUserAssessmentsButton;
